import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { GiftIcon, MinusIcon, PlayIcon, PlusIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ProductImage } from '../../../ui/ProductImage';
import { useCatalog } from '../../../contexts/CatalogContext';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { euro } from '../../../core/format/currency';
import { ordinal, policyCopy } from '../../../data/policies';
import { useSite } from '../../../contexts/SiteContext';
import { dayLong, dayShort, nextIsGift, relative } from '../../../utils/account';
import { canChangeDelivery, changeDeadline } from '../../../core/rules/subscription';
import { btnPrimary } from '../../../ui/styles';
import { isSoldOut, maxQty } from '../../../core/rules/stock';
import { shopLabels } from '../../../data/shopLabels';
import { ease } from '../../../core/format/motion';
import type { Product } from '../../../core/types/catalog';
import { DeliveryQuickActions } from './DeliveryQuickActions';
const iconBtn = 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted transition-colors duration-150 ease-soft hover:bg-sand hover:text-ink';

/** De eerstvolgende zending, op Thuis › Levering: wat erin zit, aantallen, en de snelle acties. */
export function NextDeliveryCard() {
  const {
    policy
  } = useSite();
  const {
    account,
    subscription,
    changeSubscription
  } = useSignedInAccount();
  const {
    productById
  } = useCatalog();
  const reduce = useReducedMotion();
  const sub = account.subscription;
  if (!sub) return null;

  // Producten die niet (meer) in de catalogus staan vallen weg in plaats van het scherm te breken.
  const items = sub.items.map(i => ({
    ...i,
    product: productById(i.productId)
  })).filter((i): i is typeof i & {
    product: Product;
  } => Boolean(i.product));
  const extras = sub.extras.map(id => productById(id)).filter((p): p is Product => Boolean(p));
  const total = items.reduce((s, i) => s + i.product.price * i.qty, 0) + extras.reduce((s, p) => s + p.price, 0);
  const gift = nextIsGift(sub);
  const deadline = changeDeadline(sub.nextDelivery, policy.subscription);
  // Na de wijzigingstermijn ligt deze levering vast: dezelfde regel als in de voorwaarden.
  const locked = !canChangeDelivery(sub.nextDelivery, policy.subscription);
  const setQty = (productId: string, qty: number) => subscription({
    type: 'setQty',
    productId,
    qty
  });
  const toggleExtra = (productId: string) => subscription({
    type: 'toggleExtra',
    productId
  });
  const removeItem = async (productId: string) => {
    // Het laatste product weghalen zou een leeg abonnement geven: dan liever pauzeren of stoppen.
    if (sub.items.length <= 1) {
      toast('Dit is je laatste product', {
        description: 'Wil je even niets? Pauzeer dan, of stop onderaan de pagina.'
      });
      return;
    }
    const undo = await changeSubscription({
      type: 'removeItem',
      productId
    });
    if (!undo) return;
    toast('Uit je abonnement gehaald', {
      description: productById(productId)?.name,
      action: {
        label: 'Ongedaan maken',
        onClick: () => void undo()
      }
    });
  };
  if (sub.status !== 'actief') {
    const paused = sub.status === 'gepauzeerd';
    return <section aria-labelledby="delivery-title" className="bg-shell p-5 ring-1 ring-line sm:p-7" data-id="element-5969" data-mp="h8267wghkklf">
        <p className="text-[14px] text-muted" data-id="element-5970" data-mp="ca0nxdqvwpb0">{paused ? 'Gepauzeerd' : 'Gestopt'}</p>
        <h2 id="delivery-title" className="mt-1 font-display text-[24px] leading-tight text-ink sm:text-[28px]" data-id="element-5971" data-mp="18zqdw5obkixx">
          {paused && sub.pausedUntil ? `Tot en met ${dayLong(sub.pausedUntil)}` : 'Je abonnement staat stil'}
        </h2>
        <p className="mt-2 max-w-md text-[14px] leading-relaxed text-muted" data-id="element-5972" data-mp="106eihr2p2al8">
          {paused ? 'Daarna gaat het vanzelf verder. We sturen je een week vooraf een seintje.' : 'Je producten en ritme hebben we bewaard. Opnieuw beginnen is één tik.'}
        </p>
        <button type="button" onClick={async () => {
        if (await subscription({
          type: 'resume'
        })) toast.success('Je abonnement loopt weer', {
          description: `${policyCopy.firstDelivery}.`
        });
      }} className={`${btnPrimary} mt-5`} data-id="element-5973" data-mp="1nx0qjicp7uv8">
          <PlayIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5974" data-mp="1ce884opxtoss" />
          {paused ? 'Nu al hervatten' : 'Opnieuw starten'}
        </button>
      </section>;
  }
  return <section aria-labelledby="delivery-title" className="bg-shell ring-1 ring-line" data-id="element-5975" data-mp="13i0ml7yphw27">
      <div className="p-5 sm:p-7" data-id="element-5976" data-mp="1r6adupeovc0q">
        <div className="flex items-start justify-between gap-4" data-id="element-5977" data-mp="1v72l5d4036ud">
          <div className="min-w-0" data-id="element-5978" data-mp="1v6aqc4i89d55">
            <h2 id="delivery-title" className="text-[14px] text-muted" data-id="element-5979" data-mp="1hfxis39900gc">
              Volgende levering · <span className="inline-block first-letter:uppercase" data-id="element-5980" data-mp="1mesbpy0xj5e6">{relative(sub.nextDelivery)}</span>
            </h2>
            <AnimatePresence mode="wait" initial={false} data-id="element-5981" data-mp="2a3tdj2f9biah">
              <motion.p key={sub.nextDelivery} aria-live="polite" initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.2,
              ease
            }} className="mt-1 font-display text-[30px] leading-tight text-ink sm:text-[40px]" data-id="element-5982" data-mp="2a29y1e1fdb9r">
                <span className="capitalize" data-id="element-5983" data-mp="2t1mtbhp7x19v">{dayLong(sub.nextDelivery)}</span>
              </motion.p>
            </AnimatePresence>
            <p className="mt-1 text-[14px] text-muted" data-id="element-5984" data-mp="25baoifp9rkni">
              {locked ? 'Deze levering ligt vast: Sarah pakt hem nu in. Wijzigingen gelden voor de levering daarna.' : `Aanpassen kan tot en met ${dayShort(deadline)}.`}
            </p>
          </div>
        </div>

        {gift && <p className="mt-4 flex items-center gap-2 bg-blush/50 px-3 py-2.5 text-[14px] text-ink" data-id="element-5985" data-mp="3qodg89f33k0s">
            <GiftIcon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-5986" data-mp="3lp99rqjjo0e5" />
            Dit is je {ordinal(sub.deliveriesDone + 1)} levering: er zit een cadeau bij.
          </p>}

        <ul className="mt-6 divide-y divide-line border-y border-line" aria-label="In deze levering" data-id="element-5987" data-mp="1lw7m8f2fcb0">
            {items.map(({
          product,
          qty,
          productId
        }) => <li key={productId} className="flex items-center gap-3 py-4 sm:gap-4" data-id="element-5988" data-mp="6j64280vgo8c">
                <span className="h-14 w-14 shrink-0 bg-sand" data-id="element-5989" data-mp="bjtwmy4r1d4o">
                  <ProductImage src={product.image} alt="" className="h-full w-full p-1" data-id="element-5990" data-mp="3a2qzyty8leau" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-5991" data-mp="3f23wk6ewtzqm">
                  <span className="block truncate text-[15px] text-ink" data-id="element-5992" data-mp="3eypxp85gg8nx">{product.name}</span>
                  <span className="block text-[13px] text-muted" data-id="element-5993" data-mp="3jyc7t8pzl8si">
                    {euro(product.price * qty)}
                    {/* Uitverkocht: Sarah vult dit aan of belt; meer toevoegen kan niet. */}
                    {isSoldOut(product) && <span className="text-clay" data-id="element-5994" data-mp="2wa1agfji1r8z"> · {shopLabels.soldOutShort}</span>}
                  </span>
                </span>
                <span className="flex items-center rounded-full ring-1 ring-line" role="group" aria-label={`Aantal ${product.name}`} data-id="element-5995" data-mp="2opqtwsx3lq7h">
                  <button type="button" onClick={() => void setQty(productId, qty - 1)} disabled={locked || qty <= 1} aria-label={`Eén ${product.name} minder`} className="flex h-10 w-10 items-center justify-center text-ink disabled:text-muted/40" data-id="element-5996" data-mp="2jq4ecctsrxvi">
                    <MinusIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-5997" data-mp="20zwga94yfxm6" />
                  </button>
                  <span className="w-5 text-center text-[14px] tabular-nums" aria-live="polite" data-id="element-5998" data-mp="1w0a3u1wyalef">
                    {qty}
                  </span>
                  <button type="button" onClick={() => void setQty(productId, qty + 1)}
            // Zelfde grens als de winkelmand: niet boven de voorraad of het maximum per product.
            disabled={locked || qty >= maxQty(product)} aria-label={`Eén ${product.name} meer`} className="flex h-10 w-10 items-center justify-center text-ink disabled:text-muted/40" data-id="element-5999" data-mp="1m4ggdnhp17hv">
                    <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-6000" data-mp="30zougsgnr3ot" />
                  </button>
                </span>
                <button type="button" onClick={() => void removeItem(productId)} disabled={locked} aria-label={`${product.name} uit je abonnement halen`} className={`${iconBtn} disabled:pointer-events-none disabled:opacity-40`} data-id="element-6001" data-mp="2n78nxhojsnj5">
                  <XIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6002" data-mp="249xonx2x1u3p" />
                </button>
              </li>)}
            <AnimatePresence initial={false} data-id="element-6003" data-mp="28ahjuv3xinn7">
              {extras.map(p => <motion.li key={p.id} initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            height: 0
          }} animate={reduce ? {
            opacity: 1
          } : {
            opacity: 1,
            height: 'auto'
          }} exit={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2,
            ease
          }} className="overflow-hidden" data-id="element-6004" data-mp="1pmqlwenl42fw">
                  <div className="flex items-center gap-3 py-4 sm:gap-4" data-id="element-6005" data-mp="28l2y699slsfp">
                    <span className="h-14 w-14 shrink-0 bg-sand" data-id="element-6006" data-mp="23njhz3tjb5l4">
                      <ProductImage src={p.image} alt="" className="h-full w-full p-1" data-id="element-6007" data-mp="23l73j0mzjdct" />
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-6008" data-mp="2dct8if8qcg74">
                      <span className="block truncate text-[15px] text-ink" data-id="element-6009" data-mp="2ddcuq85exqqy">{p.name}</span>
                      <span className="block text-[13px] text-clay" data-id="element-6010" data-mp="2rddvzoplb06y">Eenmalig · {euro(p.price)}</span>
                    </span>
                    <button type="button" onClick={() => void toggleExtra(p.id)} disabled={locked} aria-label={`${p.name} weer weghalen uit deze levering`} className={`${iconBtn} disabled:pointer-events-none disabled:opacity-40`} data-id="element-6011" data-mp="2mbeqa86dnr6u">
                      <XIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6012" data-mp="1tsj59oeqogc6" />
                    </button>
                  </div>
                </motion.li>)}
            </AnimatePresence>
        </ul>

        <p className="mt-4 flex items-baseline justify-between gap-4 text-[15px]" data-id="element-6013" data-mp="2mgnh6rnwlpnb">
          <span className="text-muted" data-id="element-6014" data-mp="2hfr9e30fljcm">Totaal, altijd zonder verzendkosten</span>
          <span className="tabular-nums text-ink" data-id="element-6015" data-mp="2cir3pt7huim1">{euro(total, {
            cents: true
          })}</span>
        </p>

        <div className="mt-6" data-id="element-6016" data-mp="1625lel6b5nso">
          <DeliveryQuickActions data-id="element-6017" data-mp="163gnmgk3unho" />
        </div>
      </div>
    </section>;
}